import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { environment } from 'environments/environment';
import { GlobalConstants } from '../constants/global-constants';
import { AuthStatus } from "../auth-status.enum";
import { InteractionStatus } from "@azure/msal-browser";

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  trialsLoaded = new BehaviorSubject<boolean>(false);
  rolesLoaded = new BehaviorSubject<boolean>(false);
  authStatus = new BehaviorSubject<AuthStatus>(AuthStatus.Unauthenticated);

  constructor(private readonly http: HttpClient, private readonly router: Router, private readonly authService: MsalService, private readonly msalBroadcastService: MsalBroadcastService) {
    this.msalBroadcastService.inProgress$.subscribe((status) => {

      if (status === InteractionStatus.Startup && this.loggedIn()) {
        this.authStatus.next(AuthStatus.Authenticated);

        // In case of a page refresh, check if roles have been loaded
        if (this.rolesLoaded)
          this.rolesLoaded.next(true);
      }
    });
  }

  /**
 * This method logs a user out by clearing the local AuthToken and redirecting the user to the login page
 * if shouldRedirect = true
 * @param shouldRedirect
 */
  logout(shouldRedirect: boolean = true) {
    this.authStatus.next(AuthStatus.Unauthenticated);
    this.rolesLoaded.next(false);

    const isBypassAuth = this.isBypassAuthenticated();

    if (!isBypassAuth) {
      this.authService.logoutRedirect({
        onRedirectNavigate: () => {
          return false;
        }
      });
    }

    sessionStorage.removeItem(GlobalConstants.bypassTokenStorageKey);

    // Cleared here so the inactivity clock starts fresh on the next sign-in. Left
    // behind, a stale timestamp survives in this tab's session storage and can time
    // the user straight back out again (AB#34659).
    sessionStorage.removeItem(GlobalConstants.lastActionStorageKey);

    let expenseOrderBy =  localStorage.getItem("order_by");
    localStorage.clear();
    localStorage.setItem("order_by", expenseOrderBy)

    if (shouldRedirect) {
      void this.router.navigate(['/']);
    }
  }

  isBypassAuthenticated(): boolean {
    return sessionStorage.getItem(GlobalConstants.bypassTokenStorageKey) !== null;
  }

  /**
   * This method can be called to check if a users roles have been stored in local storage
   */
  rolesAreStored(): boolean {
    const userRolesPayload = localStorage.getItem(GlobalConstants.userRolesStorageKey);
    return userRolesPayload !== null && userRolesPayload !== undefined;
  }

  getAssignedTrialFromApi(): Observable<CachedAssignedTrials[]> {
    if (this.trialsLoaded.getValue()) {
      return of(JSON.parse(localStorage.getItem(GlobalConstants.userTrialsStorageKey)) as CachedAssignedTrials[]);
    }

    return this.http.get<CachedAssignedTrials[]>(environment.apiUrl + '/access/getTrials').pipe(map(trials => {
      localStorage.setItem(GlobalConstants.userTrialsStorageKey, JSON.stringify(trials));
      this.trialsLoaded.next(true);
      return trials;
    }));
  }

  getRolesFromApi(): Observable<any> {
    return this.http.get<CachedRoles>(environment.apiUrl + '/access/getRoles').pipe(map(item => {
      localStorage.setItem(GlobalConstants.userClaimsStorageKey, JSON.stringify(item.userClaims));
      localStorage.setItem(GlobalConstants.userRolesStorageKey, JSON.stringify(item.userRoles));
      this.rolesLoaded.next(true);
    }));
  }

  hasAnyRole(roles: string): boolean {
    const userRolesPayload = localStorage.getItem(GlobalConstants.userRolesStorageKey);
    const userRolesPayloadArray = (JSON.parse(userRolesPayload) as string[] || []).map(r => r.toLowerCase().trim());

    const rolesArray = roles.split(',').map(role => role.toLowerCase().trim());

    return userRolesPayloadArray.some(userRole => rolesArray.includes(userRole));
  }

  /**
   * Determines whether the current user is an endpoint data editor for a specified trial.
   *
   * @param {string} trialId - The ID of the trial to check.
   * @return {boolean} True if the user is an endpoint data editor for the specified trial, otherwise false.
   */
  isEndpointDataEditorForTrial(trialId: string): boolean {
    const assignedTrials = JSON.parse(localStorage.getItem(GlobalConstants.userTrialsStorageKey)) as CachedAssignedTrials[];
    return assignedTrials.some(trial => trial.trialId === trialId && trial.endpointDataEditor);
  }

  hasPermission(permission: string): boolean{
    const userPermissions = JSON.parse(localStorage.getItem(GlobalConstants.userClaimsStorageKey)) as string[];
    return userPermissions.includes(permission);
  }

  /**
   * Does the current user have a particular role?
   * @param role
   */
  hasRole(role: string): boolean {
    return this.hasAnyRole(role);
  }

  hasNoRoles(): boolean {
    const userClaimsPayload = localStorage.getItem(GlobalConstants.userClaimsStorageKey);
    const userClaimsPayloadArray = JSON.parse(userClaimsPayload) as string[] || [];

    return userClaimsPayloadArray.length === 0;
  }

  getDisplayName(): string {
    const accounts = this.authService.instance.getAllAccounts();
    return accounts.length > 0 ? accounts[0].name || 'Unknown' : 'Unknown';
  }

  loggedIn() {
    const msalLoggedIn = this.authService.instance.getAllAccounts().length > 0 && localStorage.getItem(GlobalConstants.userClaimsStorageKey) !== null;
    return msalLoggedIn || this.isBypassAuthenticated();
  }
}

interface CachedRoles { userClaims: string[]; userRoles: string[] }

interface CachedAssignedTrials { trialId: string, designatedContact: boolean, projectManager: boolean, endpointDataEditor: boolean }[];
